import { useEffect, useMemo, useRef, useState } from "react";
import { CalendarDays, ChevronDown, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { fmtSaleDate } from "@/lib/applyApi";
import { timeLabel, todayCompany } from "@/lib/mountainTime";
import { closeShort } from "@/lib/brand";

export const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
export const toDate = (s) => (s ? new Date(`${s}T12:00:00`) : undefined);
// Today's date in Mountain Time, as a local Date for the calendar.
export const todayMt = () => { const d = toDate(todayCompany()); d.setHours(0, 0, 0, 0); return d; };

function DayCount({ date, n }) {
  return (
    <>
      <span>{date.getDate()}</span>
      {n > 0 && <span data-testid={`day-count-${iso(date)}`} className="text-[9px] leading-none font-semibold text-amber-800 group-aria-selected:text-white">{n} {n === 1 ? "horse" : "horses"}</span>}
    </>
  );
}

// Calendar opens inline (not in a portal) so it works inside the Horse Workspace sheet on iPhone Safari.
// Closes on date pick, the X button, Escape, or a tap outside; scrolls itself clear of sticky bars.
export function DatePick({ testId, value, onChange, placeholder, disabledBefore, marks }) {
  const [open, setOpen] = useState(false);
  const [days, setDays] = useState({});
  const [peek, setPeek] = useState(null);
  const box = useRef(null);
  const cal = useRef(null);
  // Stable per `days` so hover (peek) re-renders don't remount the day contents mid-tap (which swallowed the click on touch).
  const dayComponents = useMemo(() => ({ DayContent: ({ date }) => <DayCount date={date} n={days[iso(date)]?.length} /> }), [days]);
  // marks: optional loader of {"YYYY-MM-DD": [{key, name, time}]} — fetched fresh every time the calendar opens.
  useEffect(() => { if (open && marks) { marks().then(setDays).catch(() => {}); } if (!open) setPeek(null); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === "Escape") { e.stopImmediatePropagation(); e.preventDefault(); setOpen(false); } };
    document.addEventListener("pointerdown", away, true);
    window.addEventListener("keydown", esc, true);  // window capture runs before the sheet's Escape handler
    cal.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    return () => { document.removeEventListener("pointerdown", away, true); window.removeEventListener("keydown", esc, true); };
  }, [open]);
  return (
    <div ref={box} className="w-full">
      <Button type="button" data-testid={testId} variant="outline" aria-expanded={open} onClick={() => setOpen((o) => !o)} className="w-full h-11 justify-start font-normal">
        <CalendarDays className="h-4 w-4 mr-2 shrink-0" /><span className="truncate">{value ? fmtSaleDate(iso(value)) : placeholder}</span>
      </Button>
      {open && (
        <div ref={cal} data-testid={`${testId}-calendar`} className="mt-1 w-fit max-w-full rounded-md border border-stone-200 bg-white shadow-md scroll-my-24">
          <div className="flex justify-end px-1.5 pt-1.5 -mb-2">
            <button type="button" data-testid={`${testId}-calendar-close`} aria-label="Close calendar" onClick={() => setOpen(false)}
              className="h-8 w-8 inline-flex items-center justify-center rounded-full text-stone-600 hover:bg-stone-100"><X className="h-4 w-4" /></button>
          </div>
          <Calendar mode="single" selected={value} disabled={disabledBefore ? { before: disabledBefore } : undefined} defaultMonth={value || disabledBefore}
            onSelect={(d) => { if (d) onChange(d); }}
            // A date with horses stays open so its details remain visible (tap on mobile, also when already selected); others close as before.
            onDayClick={(d, m) => { if (m?.disabled) return; const k = iso(d); if (days[k]?.length) setPeek(k); else setOpen(false); }}
            {...(marks ? { onDayMouseEnter: (d) => days[iso(d)]?.length && setPeek(iso(d)), components: dayComponents,
              classNames: { day: "h-10 w-8 p-0 font-normal rounded-md hover:bg-accent hover:text-accent-foreground aria-selected:opacity-100 inline-flex flex-col items-center justify-center group" } } : {})} />
          {marks && peek && days[peek]?.length > 0 && (
            <div data-testid={`${testId}-day-details`} className="border-t border-stone-100 px-3 py-2 text-xs max-w-[17rem]">
              <p className="font-semibold text-stone-800">{fmtSaleDate(peek)} · {days[peek].length} {days[peek].length === 1 ? "horse" : "horses"} finishing</p>
              <ul className="mt-1 space-y-0.5">{days[peek].map((x) => <li key={x.key} className="flex justify-between gap-2 text-stone-700"><span className="truncate">{x.name}</span><span className="shrink-0 text-stone-500">{x.time}</span></li>)}</ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const TIME_OPTIONS = Array.from({ length: 96 }, (_, i) => `${String(Math.floor(i / 4)).padStart(2, "0")}:${String((i % 4) * 15).padStart(2, "0")}`);

// Explicit time choice: never fills in the current time (iOS does that for empty <input type="time">).
export function TimePick({ testId, value, onChange, placeholder = "Select start time", disabled }) {
  const opts = value && !TIME_OPTIONS.includes(value) ? [value, ...TIME_OPTIONS] : TIME_OPTIONS;
  return (
    <div className="relative w-full">
      <select data-testid={testId} aria-label={placeholder} value={value || ""} disabled={disabled} onChange={(e) => onChange(e.target.value)}
        className={`w-full h-11 appearance-none rounded-md border border-stone-200 bg-white pl-3 pr-8 text-sm ${value ? "text-stone-900" : "text-stone-500"} disabled:opacity-50`}>
        <option value="" disabled>{placeholder}</option>
        {opts.map((t) => <option key={t} value={t}>{timeLabel(t)} {closeShort().replace(/^\S+ [AP]M /, "")}</option>)}
      </select>
      <ChevronDown className="pointer-events-none absolute right-2.5 top-3.5 h-4 w-4 text-stone-400" />
    </div>
  );
}
